import {
  $convertFromMarkdownString,
  type TextMatchTransformer,
  TRANSFORMERS,
  type Transformer,
} from "@lexical/markdown";
import { $restoreEditorState } from "@lexical/utils";
import type { LexicalEditor, LexicalNode } from "lexical";
import { isEmpty } from "lodash-es";
import { CustomEmojiNode } from "../nodes/custom-emoji/custom-emoji-node";
import { isMarkdownDesign, type SerialisedEditorDesign } from "../types/serialised-editor-design";

// Node creation is injected so the same transformers can build either the MUI nodes the editor
// renders, or the plain nodes a headless editor uses.
export type DesignNodeFactories = {
  designHuddle: (
    account_id: string,
    project_id: string | undefined,
    preview_url: string,
    proportionOfMaxWidth: number,
    aspect: number,
  ) => LexicalNode;
  image: (url: string, proportionOfMaxWidth: number, aspect: number) => LexicalNode;
  customEmoji: (name: string) => LexicalNode;
};

// These only ever run on import, so there is nothing for the typing-shortcut regex to match.
const NEVER = /(?=a)(?!a)/;

const HUDDLE_REGEXP =
  /!(?:\[[^[]*)\]\((https:\/\/images[^.]+\.changeengine.com\/([^/]+)\/[^/]+\/[^/]+\/[^/]+\?p=([^)]+))\)/;

const IMAGE_REGEXP = /!(?:\[([^[]*)\])(?:\(([^(]+)\))/;

// An image is a block of its own in chat, so it replaces the paragraph markdown put it in, whereas
// email and sms keep it inline.
const imageTransformer = (
  format: "email" | "chat" | "sms",
  importRegExp: RegExp,
  create: (match: RegExpMatchArray) => LexicalNode,
): TextMatchTransformer => ({
  importRegExp,
  regExp: NEVER,
  replace: (textNode, match) => {
    const target = format === "chat" ? (textNode.getParent() ?? textNode) : textNode;
    target.replace(create(match));
  },
  type: "text-match",
  dependencies: [],
});

const EMOJI = (create: DesignNodeFactories["customEmoji"]): TextMatchTransformer => ({
  importRegExp: /:([a-zA-Z0-9_+-]+):/,
  regExp: NEVER,
  replace: (textNode, match) => {
    textNode.replace(create(match[1]!));
  },
  type: "text-match",
  dependencies: [],
});

const designTransformers = (
  editor: LexicalEditor,
  format: "email" | "chat" | "sms",
  create: DesignNodeFactories,
): Transformer[] => [
  imageTransformer(format, HUDDLE_REGEXP, (match) => {
    const [, preview, account_id, project_id] = match;
    return create.designHuddle(account_id!, project_id, preview!, 1, 1);
  }),
  imageTransformer(format, IMAGE_REGEXP, (match) => create.image(match[2]!, 1, 1)),
  // Creating a node the editor has not registered throws, and only chat registers custom emoji
  // (see EDITOR_NODES), so anywhere else leave the shortcode as the text it came from.
  ...(editor.hasNode(CustomEmojiNode) ? [EMOJI(create.customEmoji)] : []),
  ...TRANSFORMERS,
];

// Puts a design into an editor, whichever kind of design it is. Shared by loadDesign and
// loadDesignHeadless, which differ only in the nodes they can build and in how an image's aspect
// ratio is obtained, both of which arrive as `create`. Must run inside an editor update.
export const $loadDesignInto = (
  editor: LexicalEditor,
  design: SerialisedEditorDesign | undefined,
  format: "email" | "chat" | "sms",
  create: DesignNodeFactories,
): void => {
  if (isMarkdownDesign(design))
    $convertFromMarkdownString(design.markdown, designTransformers(editor, format, create));
  else if (!isEmpty(design)) $restoreEditorState(editor, editor.parseEditorState(design));
};
